import React, { useContext, useEffect, useState } from 'react';
import propTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl, FormattedMessage } from 'react-intl';
import * as Sentry from '@sentry/browser';
import { Button } from '../../../components/buttons';
import { AppleIconButton } from '../../../assets/icons/iconRepository';
import { QueryStringContext } from '../../../contexts/QueryStringContext';
import GlobalTheme from '../../../styled/GlobalTheme';
import AuthService from '../../../services/auth/AuthService';
import { WebViewContext } from '../../../contexts/WebViewContext';
import { UserSettingsContext } from '../../../contexts/UserSettingsContext';
import { generateRandomString } from '../../../utils';
import { QUERY_KEYS, QUERY_VALUES } from '../../../utils/constants/settings';

const AppleButtonStyled = styled(Button)`
&& {
  width: 100%;
  background-color: black;
  padding: 15px 10px;

  background-image: url('${(props) => props.image}');
  background-repeat: no-repeat;
  background-position: left;
  height: 28px;

  :hover {
    background-color: black;
  }

  ${GlobalTheme.breakpoints.up('lg')} {
    background-size: 6%;
    background-position-x: ${(props) => (props.languaje === 'en' ? '12%;' : '8%;')};
    font-size: 16px;
  }

  ${GlobalTheme.breakpoints.down('lg')} {
    background-size: 6%;
    background-position-x: ${(props) => (props.languaje === 'en' ? '12%;' : '8%;')};
    font-size: 12px;
    ${({ isApp, languaje }) => isApp && `
      background-size: 8%;
      background-position-x: ${languaje === 'en' ? '8%;' : '4%;'};
      font-size: 12px;
      padding: 10px 10px 10px 24px;
      height: auto;
    `}
  }

  ${GlobalTheme.breakpoints.down('md')} {
    background-size: 6%;
    background-position-x: ${(props) => (props.languaje === 'en' ? '13%;' : '8%;')};
    font-size: 16px;
    ${({ isApp, languaje }) => isApp && `
      background-size: 8%;
      font-size: 12px;
      background-position-x: ${languaje === 'en' ? '8%;' : '4%;'};
    `}
  }

  ${GlobalTheme.breakpoints.down('sm')} {
    background-size: 5%;
    background-position-x: ${(props) => (props.languaje === 'en' ? '9%;' : '9%;')};
    font-size: 14px;
    ${({ isApp }) => isApp && `
      font-size: 10px;
      padding: 8px 8px 8px 26px;
      height: auto;
    `}
  }

  ${GlobalTheme.breakpoints.down('xs')} {
    background-size: 6.5%;
    background-position-x: ${(props) => (props.languaje === 'en' ? '14%;' : '9%;')};
    font-size: 12px;
    ${({ isApp }) => isApp && `
      font-size: 10px;
    `}
  }
  @media (max-width: 1366px) and (orientation: landscape) {
    ${({ isApp, languaje }) => isApp && `
      background-size: 8%;
      background-position-x: ${languaje === 'en' ? '8%;' : '4%;'};
      font-size: 12px;
      padding: 10px 10px 10px 24px;
      height: auto;
    `}
  }
}
`;

const AppleButton = ({
  PromoCode, wantsFree, disabled, buttonType,
}) => {
  const intl = useIntl();
  const { subsCurrency, countryCode, locale } = useContext(UserSettingsContext);
  const [randomString, setRandomString] = useState('');
  const { isApp, isAppIOS } = useContext(WebViewContext);
  const { getQueryStringVal } = useContext(QueryStringContext);

  useEffect(() => {
    const rStr = generateRandomString(16);
    setRandomString(rStr);
  }, []);

  const appleFlowExecution = async () => {
    try {
      let promocode = '';
      if (PromoCode) {
        promocode = PromoCode.isValid ? PromoCode.code : '';
      }
      const typeOfTrial = getQueryStringVal(QUERY_KEYS.tt);
      const isOptIn = (typeOfTrial === QUERY_VALUES.tt);
      const lsPayload = {
        wantsEmails: true,
        wantsFree,
        language: locale,
        promocode,
        currencyCode: subsCurrency,
        countryCode,
        isOptIn,
      };
      AuthService.setUserData(lsPayload);
      await AuthService.initApple(randomString);
    } catch (e) {
      Sentry.captureException(e);
    }
  };
  return (
    <>
      <AppleButtonStyled
        onClick={() => { appleFlowExecution(); }}
        disabled={disabled}
        image={AppleIconButton}
        isApp={isApp || isAppIOS}
        languaje={intl.locale}
      >
        {buttonType === 'signin' && (
          <FormattedMessage
            id="signin.apple.button"
            defaultMessage="Sign in with Apple"
          />
        )}
        {buttonType === 'signup' && (
          <FormattedMessage
            id="signup.apple.button"
            defaultMessage="Sign in with Apple"
          />
        )}
      </AppleButtonStyled>
    </>
  );
};

AppleButton.propTypes = {
  PromoCode: propTypes.instanceOf(Object),
  wantsFree: propTypes.bool,
  buttonType: propTypes.string,
  disabled: propTypes.bool,
};

AppleButton.defaultProps = {
  PromoCode: {},
  wantsFree: false,
  buttonType: 'signin',
  disabled: false,
};

export default AppleButton;
